import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { UploadCloud, X, FileText, CheckCircle2, ArrowRight, Loader2, Film, ScrollText } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { usePageTitle } from "../components/Shared";
import { useLang } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COPY = {
  fr: {
    meta: "Inscription au concours",
    eyebrow: "Inscription candidates",
    closedTitle: "Les inscriptions sont closes",
    closedText: "Les inscriptions à ce concours sont terminées. Suivez les votes sur la page du concours.",
    viewContest: "Voir le concours",
    nom: "Nom *",
    prenom: "Prénom *",
    email: "Adresse email *",
    tel: "Téléphone / WhatsApp *",
    age: "Âge *",
    ville: "Ville de résidence *",
    bio: "Biographie * (min. 30 caractères)",
    bioPh: "Parlez-nous de vous : parcours, passions, projets…",
    motivation: "Votre motivation * (min. 20 caractères)",
    motivationPh: "Pourquoi participez-vous à ce concours ?",
    photo: "Photographie * (JPG, PNG, WebP — max 5 Mo)",
    photoDrop: "Ajoutez votre plus belle photo",
    videoFile: "Vidéo de présentation (MP4, WebM — max 50 Mo)",
    videoFileDrop: "Ou importez un fichier vidéo",
    videoUrl: "Lien vidéo (YouTube, TikTok… — facultatif)",
    videoUrlPh: "https://youtube.com/watch?v=…",
    videoOr: "Fichier vidéo ou lien : au moins l'un des deux est recommandé.",
    consentRules: "J'ai lu et j'accepte le règlement du concours. *",
    consentRights: "J'autorise DOC TOGO à publier ma photo, ma vidéo et ma biographie dans le cadre du concours. *",
    submit: "Envoyer ma candidature",
    sending: "Envoi en cours…",
    successTitle: "Candidature envoyée !",
    successText: "Notre équipe va étudier votre candidature. Vous recevrez un email dès qu'elle sera validée. Bonne chance !",
    viewContestBtn: "Suivre le concours",
    errorGeneric: "Une erreur est survenue. Vérifiez les champs et réessayez.",
    noContest: "Aucun concours n'accepte d'inscriptions pour le moment.",
  },
  en: {
    meta: "Contest registration",
    eyebrow: "Contestant registration",
    closedTitle: "Registration is closed",
    closedText: "Registration for this contest has ended. Follow the votes on the contest page.",
    viewContest: "View the contest",
    nom: "Last name *",
    prenom: "First name *",
    email: "Email address *",
    tel: "Phone / WhatsApp *",
    age: "Age *",
    ville: "City of residence *",
    bio: "Biography * (min. 30 characters)",
    bioPh: "Tell us about yourself: background, passions, projects…",
    motivation: "Your motivation * (min. 20 characters)",
    motivationPh: "Why are you entering this contest?",
    photo: "Photograph * (JPG, PNG, WebP — max 5 MB)",
    photoDrop: "Add your best photo",
    videoFile: "Presentation video (MP4, WebM — max 50 MB)",
    videoFileDrop: "Or upload a video file",
    videoUrl: "Video link (YouTube, TikTok… — optional)",
    videoUrlPh: "https://youtube.com/watch?v=…",
    videoOr: "Video file or link: at least one of the two is recommended.",
    consentRules: "I have read and accept the contest rules. *",
    consentRights: "I authorize DOC TOGO to publish my photo, video and biography as part of the contest. *",
    submit: "Submit my application",
    sending: "Sending…",
    successTitle: "Application sent!",
    successText: "Our team will review your application. You will receive an email as soon as it is validated. Good luck!",
    viewContestBtn: "Follow the contest",
    errorGeneric: "Something went wrong. Check the fields and try again.",
    noContest: "No contest is accepting applications at the moment.",
  },
};

const inputCls =
  "w-full rounded-xl border border-brand/15 bg-white px-4 py-3 text-base sm:text-sm text-slate-900 placeholder:text-slate-400 outline-none transition-[border-color,box-shadow] duration-300 focus:border-brand focus:ring-2 focus:ring-brand/15";
const labelCls = "mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500";

const FilePicker = ({ testid, label, hint, accept, file, setFile, icon: Icon }) => {
  const ref = useRef(null);
  return (
    <div>
      <span className={labelCls}>{label}</span>
      <div
        data-testid={testid}
        onClick={() => ref.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); if (e.dataTransfer.files[0]) setFile(e.dataTransfer.files[0]); }}
        className="cursor-pointer rounded-2xl border-2 border-dashed border-brand/25 bg-brand-light/40 px-6 py-6 text-center transition-[border-color,background-color] duration-300 hover:border-brand hover:bg-brand-light"
      >
        <Icon className="mx-auto h-7 w-7 text-brand" />
        <p className="mt-2 text-sm font-semibold text-slate-700">{hint}</p>
        <input ref={ref} type="file" accept={accept} data-testid={`${testid}-input`} className="hidden"
          onChange={(e) => e.target.files[0] && setFile(e.target.files[0])} />
      </div>
      {file && (
        <div className="mt-3 flex items-center justify-between rounded-xl border border-brand/10 bg-ivory px-4 py-2.5 text-sm">
          <span className="flex items-center gap-2.5 truncate text-slate-700">
            <FileText className="h-4 w-4 shrink-0 text-brand" />
            <span className="truncate">{file.name}</span>
            <span className="shrink-0 text-xs text-slate-400">{(file.size / 1024 / 1024).toFixed(1)} Mo</span>
          </span>
          <button type="button" data-testid={`${testid}-remove`} onClick={() => setFile(null)} aria-label="Retirer"
            className="ml-3 rounded-full p-1 text-slate-400 transition-colors hover:bg-crimson/10 hover:text-crimson">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export default function ConcoursInscription() {
  const { lang } = useLang();
  const c = COPY[lang];
  usePageTitle(c.meta);
  const [event, setEvent] = useState(undefined);
  const [form, setForm] = useState({ nom: "", prenom: "", email: "", telephone: "", age: "", ville: "", bio: "", motivation: "", video_url: "" });
  const [photo, setPhoto] = useState(null);
  const [video, setVideo] = useState(null);
  const [consentRules, setConsentRules] = useState(false);
  const [consentRights, setConsentRights] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios.get(`${API}/concours/current`)
      .then((r) => setEvent(r.data))
      .catch(() => setEvent(null));
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!photo) { toast.error(c.photo); return; }
    if (!consentRules || !consentRights) { toast.error("*"); return; }
    setLoading(true);
    try {
      const data = new FormData();
      Object.entries(form).forEach(([k, v]) => data.append(k, v));
      data.append("accepte_reglement", String(consentRules));
      data.append("accepte_droits", String(consentRights));
      data.append("photo", photo);
      if (video) data.append("video", video);
      await axios.post(`${API}/concours/${event.event.slug}/candidatures`, data, { timeout: 120000 });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : c.errorGeneric);
    } finally {
      setLoading(false);
    }
  };

  if (event === undefined) {
    return <div className="grid min-h-screen place-items-center bg-brand-deep pt-20 text-sm text-white/50">…</div>;
  }

  if (event === null || !event.inscriptions_ouvertes) {
    return (
      <div data-testid="inscription-closed" className="grid min-h-screen place-items-center bg-ivory px-6 pt-20">
        <div className="max-w-md text-center">
          <ScrollText className="mx-auto h-12 w-12 text-brand/40" />
          <h1 className="mt-6 font-display text-3xl font-extrabold text-slate-900">{c.closedTitle}</h1>
          <p className="mt-3 text-slate-500">{event === null ? c.noContest : c.closedText}</p>
          {event !== null && (
            <Link to="/concours" data-testid="inscription-view-contest"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-brand px-7 py-3.5 font-display font-bold text-white transition-colors hover:bg-brand-hover">
              {c.viewContest}
            </Link>
          )}
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div data-testid="inscription-success" className="grid min-h-screen place-items-center bg-brand-deep px-6 pb-24 pt-40 text-white">
        <div className="max-w-lg text-center">
          <CheckCircle2 className="mx-auto h-16 w-16 text-gold" />
          <h1 className="mt-6 font-display text-3xl font-extrabold sm:text-4xl">{c.successTitle}</h1>
          <p className="mt-4 text-base leading-relaxed text-white/60">{c.successText}</p>
          <Link to="/concours" data-testid="inscription-success-contest"
            className="mt-10 inline-flex items-center gap-2 rounded-full bg-gold px-7 py-3.5 font-display font-bold text-brand-deep transition-[background-color,transform] duration-300 hover:bg-gold-warm hover:-translate-y-0.5">
            {c.viewContestBtn} <ArrowRight className="h-5 w-5" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="inscription-page">
      <section className="relative overflow-hidden bg-brand-deep text-white">
        <div className="pointer-events-none absolute -top-32 right-[-5%] h-96 w-96 rounded-full bg-gold/15 blur-[110px]" />
        <div className="relative mx-auto max-w-3xl px-6 pb-14 pt-32 lg:pt-40">
          <div className="mb-5 flex items-center gap-2 font-mono text-xs font-bold uppercase tracking-[0.3em] text-gold">
            <ScrollText className="h-4 w-4" /> {c.eyebrow}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.06]">{event.event.nom}</h1>
          {event.event.prix_montant > 0 && (
            <p className="mt-4 text-base text-white/60">
              {lang === "en"
                ? `Grand prize: ${event.event.prix_montant.toLocaleString("en-US")} ${event.event.prix_devise}`
                : `Grand prix : ${event.event.prix_montant.toLocaleString("fr-FR")} ${event.event.prix_devise}`}
            </p>
          )}
        </div>
      </section>

      <section className="bg-ivory py-16 sm:py-20">
        <div className="mx-auto max-w-3xl px-6">
          <Reveal>
            <form onSubmit={submit} data-testid="inscription-form" className="rounded-3xl border border-brand/10 bg-white p-7 shadow-sm sm:p-10">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label htmlFor="insc-prenom" className={labelCls}>{c.prenom}</label>
                  <input id="insc-prenom" data-testid="insc-prenom" required maxLength={80} value={form.prenom} onChange={set("prenom")} autoComplete="given-name" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="insc-nom" className={labelCls}>{c.nom}</label>
                  <input id="insc-nom" data-testid="insc-nom" required minLength={2} maxLength={80} value={form.nom} onChange={set("nom")} autoComplete="family-name" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="insc-email" className={labelCls}>{c.email}</label>
                  <input id="insc-email" type="email" data-testid="insc-email" required value={form.email} onChange={set("email")} autoComplete="email" inputMode="email" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="insc-tel" className={labelCls}>{c.tel}</label>
                  <input id="insc-tel" type="tel" data-testid="insc-tel" required minLength={6} maxLength={25} value={form.telephone} onChange={set("telephone")} autoComplete="tel" inputMode="tel" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="insc-age" className={labelCls}>{c.age}</label>
                  <input id="insc-age" type="number" min={16} max={40} data-testid="insc-age" required value={form.age} onChange={set("age")} inputMode="numeric" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="insc-ville" className={labelCls}>{c.ville}</label>
                  <input id="insc-ville" data-testid="insc-ville" required maxLength={80} value={form.ville} onChange={set("ville")} className={inputCls} />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="insc-bio" className={labelCls}>{c.bio}</label>
                  <textarea id="insc-bio" data-testid="insc-bio" required minLength={30} maxLength={2000} rows={4} value={form.bio} onChange={set("bio")} placeholder={c.bioPh} className={`${inputCls} resize-y`} />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="insc-motivation" className={labelCls}>{c.motivation}</label>
                  <textarea id="insc-motivation" data-testid="insc-motivation" required minLength={20} maxLength={2000} rows={3} value={form.motivation} onChange={set("motivation")} placeholder={c.motivationPh} className={`${inputCls} resize-y`} />
                </div>
                <div className="sm:col-span-2">
                  <FilePicker testid="insc-photo" label={c.photo} hint={c.photoDrop} accept=".jpg,.jpeg,.png,.webp" file={photo} setFile={setPhoto} icon={UploadCloud} />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="insc-video-url" className={labelCls}>{c.videoUrl}</label>
                  <input id="insc-video-url" type="url" data-testid="insc-video-url" value={form.video_url} onChange={set("video_url")} placeholder={c.videoUrlPh} className={inputCls} />
                </div>
                <div className="sm:col-span-2">
                  <FilePicker testid="insc-video" label={c.videoFile} hint={c.videoFileDrop} accept=".mp4,.webm,.mov,.m4v" file={video} setFile={setVideo} icon={Film} />
                  <p className="mt-2 text-xs text-slate-400">{c.videoOr}</p>
                </div>
                <div className="sm:col-span-2 space-y-4">
                  <label className="flex cursor-pointer items-start gap-3 text-sm text-slate-600">
                    <input type="checkbox" data-testid="insc-consent-rules" checked={consentRules} onChange={(e) => setConsentRules(e.target.checked)}
                      className="mt-1 h-4 w-4 rounded border-brand/30 accent-[#0D482B]" />
                    <span>{c.consentRules}</span>
                  </label>
                  <label className="flex cursor-pointer items-start gap-3 text-sm text-slate-600">
                    <input type="checkbox" data-testid="insc-consent-rights" checked={consentRights} onChange={(e) => setConsentRights(e.target.checked)}
                      className="mt-1 h-4 w-4 rounded border-brand/30 accent-[#0D482B]" />
                    <span>{c.consentRights}</span>
                  </label>
                </div>
              </div>
              <button type="submit" disabled={loading} data-testid="insc-submit"
                className="group mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand px-8 py-4 font-display text-base font-bold text-white transition-[background-color,transform] duration-300 hover:bg-brand-hover hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60">
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />}
                {loading ? c.sending : c.submit}
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
